import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'

import DateRangePickerInput from '@/components/ui/inputs/DateRangePickerInput'
import { PaymentType, BillingReviewStatus } from '@/core/models/consultation.model'
import { NetworkMember } from '@/core/models/network.model'

interface ConsultationExportDialogProps {
  open: boolean
  onClose: () => void
  onExport: (filters: ExportFilters) => void
  isExporting: boolean
  networkMembers: NetworkMember[]
}

export interface ExportFilters {
  startDateFrom?: string
  startDateTo?: string
  doctors?: string[]
  pds?: string[]
  paid?: boolean
  paymentType?: PaymentType
  billingReviewStatus?: BillingReviewStatus
}

export default function ConsultationExportDialog({ open, onClose, onExport, isExporting, networkMembers }: ConsultationExportDialogProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  // Initialize date range with current day as both start and end if needed
  const today = new Date()
  const [dateRange, setDateRange] = useState<[Date, Date]>([today, today])
  const [selectedDoctor, setSelectedDoctor] = useState<string>('')
  const [selectedPds, setSelectedPds] = useState<string>('')
  const [filterByPayment, setFilterByPayment] = useState<boolean>(false)
  const [paymentStatus, setPaymentStatus] = useState<boolean | undefined>(undefined)
  const [paymentType, setPaymentType] = useState<PaymentType | ''>('')
  const [billingReviewStatus, setBillingReviewStatus] = useState<BillingReviewStatus | ''>('')

  const handleExport = () => {
    const exportFilters: ExportFilters = {}

    // Only add date filters if dates are valid
    if (dateRange[0]) {
      exportFilters.startDateFrom = dateRange[0].toISOString()
    }

    if (dateRange[1]) {
      exportFilters.startDateTo = dateRange[1].toISOString()
    }

    if (selectedDoctor) {
      exportFilters.doctors = [selectedDoctor]
    }

    if (selectedPds) {
      exportFilters.pds = [selectedPds]
    }

    if (filterByPayment && paymentStatus !== undefined) {
      exportFilters.paid = paymentStatus
    }

    if (paymentType) {
      exportFilters.paymentType = paymentType as PaymentType
    }

    if (billingReviewStatus) {
      exportFilters.billingReviewStatus = billingReviewStatus as BillingReviewStatus
    }

    onExport(exportFilters)
  }

  const handleReset = () => {
    setDateRange([today, today])
    setSelectedDoctor('')
    setSelectedPds('')
    setFilterByPayment(false)
    setPaymentStatus(undefined)
    setPaymentType('')
    setBillingReviewStatus('')
  }

  const handleDateRangeChange = ({ start, end }: { start: Date; end: Date }) => {
    setDateRange([start, end])
  }

  // Filter network members by role
  const doctors = networkMembers.filter((member) => member?.member?.status === 'MEDECIN')
  const nurses = networkMembers.filter((member) => member?.member?.status === 'PDS')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('page.consultation.exportDialog.title', { defaultValue: 'Exporter les consultations' })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="subtitle1">
            {t('page.consultation.exportDialog.filterOptions', { defaultValue: 'Options de filtrage' })}
          </Typography>

          <DateRangePickerInput
            label={t('page.consultation.exportDialog.dateRange', { defaultValue: 'Période' })}
            value={dateRange}
            onChange={handleDateRangeChange}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>
                  {t('page.consultation.exportDialog.doctor', { defaultValue: 'Médecin' })}
                </InputLabel>
                <Select
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  label={t('page.consultation.exportDialog.doctor', { defaultValue: 'Médecin' })}
                >
                  <MenuItem value="">
                    {t('page.consultation.exportDialog.allDoctors', { defaultValue: 'Tous les médecins' })}
                  </MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.member._id} value={doctor.member._id}>
                      {doctor.member.userInfo.name} {doctor.member.userInfo.surname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>
                  {t('page.consultation.exportDialog.nurse', { defaultValue: 'Infirmier' })}
                </InputLabel>
                <Select
                  value={selectedPds}
                  onChange={(e) => setSelectedPds(e.target.value)}
                  label={t('page.consultation.exportDialog.nurse', { defaultValue: 'Infirmier' })}
                >
                  <MenuItem value="">
                    {t('page.consultation.exportDialog.allNurses', { defaultValue: 'Tous les infirmiers' })}
                  </MenuItem>
                  {nurses.map((nurse) => (
                    <MenuItem key={nurse.member._id} value={nurse.member._id}>
                      {nurse.member.userInfo.name} {nurse.member.userInfo.surname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <FormControlLabel
            control={<Checkbox checked={filterByPayment} onChange={(e) => setFilterByPayment(e.target.checked)} />}
            label={t('page.consultation.exportDialog.filterByPayment', { defaultValue: 'Filtrer par statut de paiement' })}
          />

          {filterByPayment && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    {t('page.consultation.exportDialog.paymentStatus', { defaultValue: 'Statut de paiement' })}
                  </InputLabel>
                  <Select
                    value={paymentStatus === undefined ? '' : paymentStatus ? 'paid' : 'unpaid'}
                    onChange={(e) => setPaymentStatus(e.target.value === 'paid' ? true : e.target.value === 'unpaid' ? false : undefined)}
                    label={t('page.consultation.exportDialog.paymentStatus', { defaultValue: 'Statut de paiement' })}
                  >
                    <MenuItem value="">
                      {t('page.consultation.exportDialog.all', { defaultValue: 'Tous' })}
                    </MenuItem>
                    <MenuItem value="paid">
                      {t('page.consultation.exportDialog.paid', { defaultValue: 'Payé' })}
                    </MenuItem>
                    <MenuItem value="unpaid">
                      {t('page.consultation.exportDialog.unpaid', { defaultValue: 'Non payé' })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>
                    {t('page.consultation.exportDialog.paymentType', { defaultValue: 'Type de paiement' })}
                  </InputLabel>
                  <Select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value as PaymentType | '')}
                    label={t('page.consultation.exportDialog.paymentType', { defaultValue: 'Type de paiement' })}
                  >
                    <MenuItem value="">
                      {t('page.consultation.exportDialog.all', { defaultValue: 'Tous' })}
                    </MenuItem>
                    <MenuItem value={PaymentType.Card}>{t('common:payment.type.card')}</MenuItem>
                    <MenuItem value={PaymentType.Cash}>{t('common:payment.type.cash')}</MenuItem>
                    <MenuItem value={PaymentType.Check}>{t('common:payment.type.check')}</MenuItem>
                    <MenuItem value={PaymentType.Transfer}>{t('common:payment.type.transfer')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <FormControl fullWidth>
            <InputLabel>
              {t('page.consultation.exportDialog.billingReview', { defaultValue: 'Revue de facturation' })}
            </InputLabel>
            <Select
              value={billingReviewStatus}
              onChange={(e) => setBillingReviewStatus(e.target.value as BillingReviewStatus | '')}
              label={t('page.consultation.exportDialog.billingReview', { defaultValue: 'Revue de facturation' })}
            >
              <MenuItem value="">
                {t('page.consultation.filter.billingReview.all', { defaultValue: 'Tous' })}
              </MenuItem>
              <MenuItem value={BillingReviewStatus.Pending}>
                {t('page.consultation.filter.billingReview.pending', { defaultValue: 'En attente' })}
              </MenuItem>
              <MenuItem value={BillingReviewStatus.Issue}>
                {t('page.consultation.filter.billingReview.issue', { defaultValue: 'Problèmes' })}
              </MenuItem>
              <MenuItem value={BillingReviewStatus.Completed}>
                {t('page.consultation.filter.billingReview.completed', { defaultValue: 'Terminés' })}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="inherit">
          {t('page.consultation.exportDialog.reset', { defaultValue: 'Réinitialiser les filtres' })}
        </Button>
        <Button onClick={onClose} color="inherit">
          {t('common:button.cancel.label')}
        </Button>
        <Button onClick={handleExport} color="primary" variant="contained" disabled={isExporting}>
          {isExporting
            ? t('page.consultation.exportDialog.exporting', { defaultValue: 'Export en cours...' })
            : t('page.consultation.export.button', { defaultValue: 'Exporter CSV' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
